import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from 'react';
import { MRT_ColumnDef, MRT_SortingState, useMaterialReactTable } from 'material-react-table';
import { Box, IconButton, MenuItem, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { openToast, openToastError } from 'src/use-cases/toast/useToast';
import { SettingsTableMain } from 'src/presentations/components/molecules/settings/table/TableMain';
import { ModalConfirm } from 'src/presentations/components/molecules/modal';
import { ServiceProviderAddressDto } from 'src/generated/services/TFinancialApi';
import { useDeleteServiceProviderAddress, useUpsertServiceProviderAddress } from 'src/use-cases/settings/useServiceProvider';
import { AddressType } from 'src/types';

interface ServiceProviderAddressTableProps {
  addresses: ServiceProviderAddressDto[];
  onEditAddress?: (address: ServiceProviderAddressDto) => void;
  serviceProviderId: number;
}

export const ServiceProviderAddressTable = forwardRef<any, ServiceProviderAddressTableProps>(({ addresses, onEditAddress, serviceProviderId }, ref) => {
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [rowToDelete, setRowToDelete] = useState<ServiceProviderAddressDto | null>(null);
  const [sorting, setSorting] = useState<MRT_SortingState>([
    {
      id: 'id',
      desc: false,
    },
  ]);
  const { mutateAsync: upsertAddress } = useUpsertServiceProviderAddress();
  const { mutateAsync: deleteAddress, isLoading: isDeleting } = useDeleteServiceProviderAddress();

  const getAddressTypeLabel = (type: string | null | undefined): string => {
    if (!type) return '';
    return type.replace(/_/g, ' ').replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.slice(1).toLowerCase());
  };

  const requiredFields = ['address', 'city', 'country', 'type', 'zipcode'];
  const handleCreateAddress = async ({ values, exitCreatingMode }: any) => {
    const errors: Record<string, string> = {};
    let hasErrors = false;
    requiredFields.forEach((field) => {
      if (!values[field]) {
        errors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
        hasErrors = true;
      }
    });
    if (hasErrors) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    const addressDto: ServiceProviderAddressDto = {
      serviceProviderId,
      address: values.address,
      city: values.city,
      state: values.state,
      zipcode: values.zipcode,
      country: values.country,
      email: values.email,
      fax: values.fax,
      phone: values.phone,
      type: values.type,
      active: true,
    };
    try {
      await upsertAddress({ serviceProviderId, address: addressDto });
      openToast({
        title: 'Address created successfully',
        toastType: 'success',
      });
      exitCreatingMode?.();
    } catch (error: any) {
      openToastError(error instanceof Error ? error : new Error('Failed to create address'));
    }
  };

  const handleUpdateAddress = async ({ row, values, exitEditingMode }: any) => {
    const errors: Record<string, string> = {};
    let hasErrors = false;
    requiredFields.forEach((field) => {
      if (!values[field]) {
        errors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
        hasErrors = true;
      }
    });
    if (hasErrors) {
      setValidationErrors(errors);
      return;
    }
    setValidationErrors({});
    const addressDto: ServiceProviderAddressDto = {
      id: row?.original?.id,
      serviceProviderId,
      address: values.address,
      city: values.city,
      state: values.state,
      zipcode: values.zipcode,
      country: values.country,
      email: values.email,
      fax: values.fax,
      phone: values.phone,
      type: values.type,
      active: true,
    };
    try {
      await upsertAddress({ serviceProviderId, address: addressDto });
      openToast({
        title: 'Address updated successfully',
        toastType: 'success',
      });
      exitEditingMode?.();
    } catch (error: any) {
      openToastError(error instanceof Error ? error : new Error('Failed to update address'));
    }
  };

  // Wrap async handlers for void return
  const handleCreateAddressVoid = (args: any) => {
    handleCreateAddress(args);
  };
  const handleUpdateAddressVoid = (args: any) => {
    handleUpdateAddress(args);
  };

  const handleDeleteAddress = async (address: ServiceProviderAddressDto) => {
    if (!address.id) {
      openToast({
        toastType: 'error',
        title: 'Error',
        description: 'Cannot delete address: missing ID',
      });
      return;
    }
    try {
      await deleteAddress({ addressId: address.id });
      openToast({
        toastType: 'success',
        title: 'Success',
        description: 'Address deleted successfully!',
      });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      openToast({
        toastType: 'error',
        title: 'Error',
        description: errorMessage,
      });
    }
  };

  const renderRowActions = useCallback(
    ({ row, table }) => (
      <Box sx={{ display: 'flex', gap: '1rem' }}>
        <Tooltip title='Edit'>
          <IconButton onClick={() => table.setEditingRow(row)}>
            <EditIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title='Delete'>
          <IconButton color='error' onClick={() => setRowToDelete(row.original)}>
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    ),
    []
  );

  const addressTypeOptions = Object.values(AddressType).map((type) => ({
    value: type,
    label: getAddressTypeLabel(type),
  }));

  const addressColumns = useMemo<MRT_ColumnDef<ServiceProviderAddressDto>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        enableEditing: false,
        size: 80,
      },
      {
        accessorKey: 'address',
        header: 'Address',
        size: 200,
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.address,
          helperText: validationErrors?.address,
          onFocus: () => {
            if (validationErrors?.address) {
              setValidationErrors({
                ...validationErrors,
                address: undefined,
              });
            }
          },
        },
      },
      {
        accessorKey: 'city',
        header: 'City',
        size: 120,
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.city,
          helperText: validationErrors?.city,
          onFocus: () => {
            if (validationErrors?.city) {
              setValidationErrors({
                ...validationErrors,
                city: undefined,
              });
            }
          },
        },
      },
      {
        accessorKey: 'state',
        header: 'State',
        size: 100,
        muiEditTextFieldProps: {
          required: false,
          error: !!validationErrors?.state,
          helperText: validationErrors?.state,
          onFocus: () => {
            if (validationErrors?.state) {
              setValidationErrors({
                ...validationErrors,
                state: undefined,
              });
            }
          },
        },
      },
      {
        accessorKey: 'zipcode',
        header: 'Postal Code',
        size: 100,
        muiEditTextFieldProps: {
          error: !!validationErrors?.zipcode,
          helperText: validationErrors?.zipcode,
          onFocus: () => {
            if (validationErrors?.zipcode) {
              setValidationErrors({
                ...validationErrors,
                zipcode: undefined,
              });
            }
          },
        },
      },
      {
        accessorKey: 'country',
        header: 'Country',
        size: 100,
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.country,
          helperText: validationErrors?.country,
          onFocus: () => {
            if (validationErrors?.country) {
              setValidationErrors({
                ...validationErrors,
                country: undefined,
              });
            }
          },
        },
      },
      {
        accessorKey: 'type',
        header: 'Address Type',
        Cell: ({ cell }) => getAddressTypeLabel(cell.getValue<string>()),
        muiEditTextFieldProps: {
          required: true,
          select: true,
          error: !!validationErrors?.type,
          helperText: validationErrors?.type,
          onFocus: () => {
            if (validationErrors?.type) {
              setValidationErrors({
                ...validationErrors,
                type: undefined,
              });
            }
          },
          children: addressTypeOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          )),
        },
      },
    ],
    [validationErrors]
  );

  const table = useMaterialReactTable({
    columns: addressColumns,
    data: addresses || [],
    enableEditing: true,
    editDisplayMode: 'row',
    createDisplayMode: 'row',
    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleCreateAddressVoid,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleUpdateAddressVoid,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    enableSorting: true,
    enableColumnActions: false,
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 'bold',
      },
    },
    displayColumnDefOptions: {
      'mrt-row-actions': {
        size: 80,
        muiTableHeadCellProps: {
          align: 'center',
        },
      },
    },
    renderEmptyRowsFallback: () => <p className='text-center py-4'>No addresses found</p>,
    renderRowActions,
  });

  useImperativeHandle(ref, () => ({
    setCreatingRow: table.setCreatingRow,
    getState: table.getState,
  }));

  return (
    <div className='overflow-x-auto'>
      <SettingsTableMain table={table} />
      <ModalConfirm
        open={!!rowToDelete}
        title='Warning'
        isLoading={isDeleting}
        onAccept={async () => {
          if (rowToDelete) {
            await handleDeleteAddress(rowToDelete);
            setRowToDelete(null);
          }
        }}
        onCancel={() => setRowToDelete(null)}
        buttonAcceptLabel='Delete'
        buttonCancelLabel='Cancel'
      >
        <p>Are you sure you want to delete this address?</p>
      </ModalConfirm>
    </div>
  );
});
